import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import organizationService from '../../services/organizationService';
import AddEditModal from './AddEditModal';
import GroupMembersModal from './GroupMembersModal';
import { addNotification } from '../../store/slices/uiSlice';
import './OrganizationStructure.css';

const OrganizationStructure = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [districts, setDistricts] = useState([]);
  const [expandedDistricts, setExpandedDistricts] = useState({});
  const [expandedZones, setExpandedZones] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'district', 'zone', 'group'
  const [selectedParent, setSelectedParent] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [activeGroupForMembers, setActiveGroupForMembers] = useState(null);
  const [error, setError] = useState(null);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  
  // Filter states - 3 cascading autocomplete selectors
  const [districtSearch, setDistrictSearch] = useState('');
  const [zoneSearch, setZoneSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [showZoneDropdown, setShowZoneDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const districtRef = useRef(null);
  const zoneRef = useRef(null);
  const groupRef = useRef(null);

  // Load data from API on mount
  useEffect(() => {
    loadOrganizationData();
  }, []);

  // Click outside handler for filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (districtRef.current && !districtRef.current.contains(event.target)) {
        setShowDistrictDropdown(false);
      }
      if (zoneRef.current && !zoneRef.current.contains(event.target)) {
        setShowZoneDropdown(false);
      }
      if (groupRef.current && !groupRef.current.contains(event.target)) {
        setShowGroupDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Apply filters when districts or filter selections change
  useEffect(() => {
    applyFilters();
  }, [districts, selectedDistrict, selectedZone, selectedGroup]);

  const applyFilters = () => {
    let filtered = districts;

    if (selectedDistrict) {
      filtered = districts.filter(d => d.id === selectedDistrict.id);
    }

    if (selectedZone) {
      filtered = filtered.map(district => {
        const matchingZone = district.zones?.find(z => z.id === selectedZone.id);
        if (!matchingZone) return null;
        return { ...district, zones: [matchingZone] };
      }).filter(Boolean);
    }

    if (selectedGroup) {
      filtered = filtered.map(district => {
        const zonesWithGroup = district.zones?.map(zone => {
          const matchingGroup = zone.groups?.find(g => g.id === selectedGroup.id);
          if (!matchingGroup) return null;
          return { ...zone, groups: [matchingGroup] };
        }).filter(Boolean) || [];
        if (zonesWithGroup.length === 0) return null;
        return { ...district, zones: zonesWithGroup };
      }).filter(Boolean);
    }

    setFilteredDistricts(filtered);
  };

  // Get available zones based on selected district
  const getAvailableZones = () => {
    if (selectedDistrict) {
      return selectedDistrict.zones || [];
    }
    // Return all zones from all districts
    return districts.flatMap(d => d.zones || []);
  };

  // Get available groups based on selected zone
  const getAvailableGroups = () => {
    if (selectedZone) {
      return selectedZone.groups || [];
    }
    if (selectedDistrict) {
      return selectedDistrict.zones?.flatMap(z => z.groups || []) || [];
    }
    // Return all groups
    return districts.flatMap(d => d.zones?.flatMap(z => z.groups || []) || []);
  };

  // Filter suggestions for district autocomplete
  const getDistrictSuggestions = () => {
    const searchLower = districtSearch.toLowerCase();
    return districts
      .filter(d => d.name.toLowerCase().includes(searchLower))
      .slice(0, 10);
  };

  // Filter suggestions for zone autocomplete
  const getZoneSuggestions = () => {
    const searchLower = zoneSearch.toLowerCase();
    const available = getAvailableZones();
    return available
      .filter(z => z.name.toLowerCase().includes(searchLower))
      .slice(0, 10);
  };

  // Filter suggestions for group autocomplete
  const getGroupSuggestions = () => {
    const searchLower = groupSearch.toLowerCase();
    const available = getAvailableGroups();
    return available
      .filter(g => g.name.toLowerCase().includes(searchLower))
      .slice(0, 10);
  };

  // Handle district selection
  const handleSelectDistrict = (district) => {
    setSelectedDistrict(district);
    setDistrictSearch(district.name);
    setShowDistrictDropdown(false);
    // Clear zone and group when district changes
    setSelectedZone(null);
    setZoneSearch('');
    setSelectedGroup(null);
    setGroupSearch('');
    // Auto-expand the selected district
    setExpandedDistricts(prev => ({ ...prev, [district.id]: true }));
  };

  // Handle zone selection
  const handleSelectZone = (zone) => {
    setSelectedZone(zone);
    setZoneSearch(zone.name);
    setShowZoneDropdown(false);
    // Clear group when zone changes
    setSelectedGroup(null);
    setGroupSearch('');
    // Auto-select parent district if not selected
    const parentDistrict = districts.find(d => d.zones?.some(z => z.id === zone.id));
    if (parentDistrict) {
      if (!selectedDistrict) {
        setSelectedDistrict(parentDistrict);
        setDistrictSearch(parentDistrict.name);
      }
      // Auto-expand the parent district and selected zone
      setExpandedDistricts(prev => ({ ...prev, [parentDistrict.id]: true }));
      setExpandedZones(prev => ({ ...prev, [zone.id]: true }));
    }
  };

  // Handle group selection
  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setGroupSearch(group.name);
    setShowGroupDropdown(false);
    // Auto-select parent zone and district if not selected
    for (const district of districts) {
      for (const zone of district.zones || []) {
        if (zone.groups?.some(g => g.id === group.id)) {
          if (!selectedDistrict) {
            setSelectedDistrict(district);
            setDistrictSearch(district.name);
          }
          if (!selectedZone) {
            setSelectedZone(zone);
            setZoneSearch(zone.name);
          }
          // Auto-expand the hierarchy to show the selected group
          setExpandedDistricts(prev => ({ ...prev, [district.id]: true }));
          setExpandedZones(prev => ({ ...prev, [zone.id]: true }));
          return;
        }
      }
    }
  };

  // Clear district filter
  const handleClearDistrict = () => {
    setSelectedDistrict(null);
    setDistrictSearch('');
    setSelectedZone(null);
    setZoneSearch('');
    setSelectedGroup(null);
    setGroupSearch('');
  };

  // Clear zone filter
  const handleClearZone = () => {
    setSelectedZone(null);
    setZoneSearch('');
    setSelectedGroup(null);
    setGroupSearch('');
  };

  // Clear group filter
  const handleClearGroup = () => {
    setSelectedGroup(null);
    setGroupSearch('');
  };

  const loadOrganizationData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await organizationService.getOrganizationTree();
      setDistricts(data);
    } catch (err) {
      console.error('Error loading organization data:', err);
      setError(t('organization.error'));
    } finally {
      setLoading(false);
    }
  };

  const toggleDistrict = (districtId) => {
    setExpandedDistricts(prev => ({
      ...prev,
      [districtId]: !prev[districtId]
    }));
  };

  const toggleZone = (zoneId) => {
    setExpandedZones(prev => ({
      ...prev,
      [zoneId]: !prev[zoneId]
    }));
  };

  const handleAddNew = (type, parent = null) => {
    setModalType(type);
    setSelectedParent(parent);
    setEditingItem(null);
    setShowAddModal(true);
  };

  const handleEdit = (type, item, parent = null) => {
    setModalType(type);
    setSelectedParent(parent);
    setEditingItem(item);
    setShowAddModal(true);
  };

  const handleDelete = async (type, id, parentId = null) => {
    const typeLabel = t(`organization.types.${type}`);
    if (!window.confirm(t('organization.confirmDelete', { type: typeLabel }))) {
      return;
    }

    try {
      if (type === 'district') {
        await organizationService.deleteDistrict(id);
      } else if (type === 'zone') {
        await organizationService.deleteZone(id);
      } else if (type === 'group') {
        await organizationService.deleteGroup(id);
      }
      
      // Reload data after successful deletion
      await loadOrganizationData();
    } catch (err) {
      console.error('Error deleting item:', err);
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message: t('organization.deleteError')
      }));
    }
  };

  const handleManageMembers = (group) => {
    setActiveGroupForMembers(group);
    setShowMemberModal(true);
  };

  const closeMemberModal = () => {
    setShowMemberModal(false);
    setActiveGroupForMembers(null);
  };

  const handleMembersUpdated = async () => {
    await loadOrganizationData();
  };

  const handleInlineRemoveAssignment = async (assignmentId) => {
    if (!window.confirm(t('organization.confirmRemoveMember'))) {
      return;
    }

    try {
      await organizationService.removeMemberFromGroup(assignmentId);
      await handleMembersUpdated();
    } catch (err) {
      console.error('Error removing assignment:', err);
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message: t('organization.removeError')
      }));
    }
  };

  const renderManagerLabel = (entity) => {
    // If entity is a member directly (has chi_name), show member info
    if (entity?.chi_name) {
      return entity.worship_id 
        ? `${entity.chi_name} (${entity.worship_id})` 
        : entity.chi_name;
    }
    // If entity has a manager (member relation), show member info
    if (entity?.manager) {
      return entity.manager.worship_id 
        ? `${entity.manager.chi_name} (${entity.manager.worship_id})` 
        : entity.manager.chi_name;
    }
    // If entity has manager_name (non-member), show the name
    if (entity?.manager_name) {
      return entity.manager_name;
    }
    // No manager assigned
    return t('organization.detail.noneAssigned');
  };

  const roleLabelMap = {
    core: t('organization.stats.core'),
    full: t('organization.stats.full'),
    associate: t('organization.stats.associate')
  };

  const groupAssignmentsByRole = (assignments = []) => {
    return assignments.reduce((acc, assignment) => {
      const role = assignment.role || 'associate';
      if (!acc[role]) {
        acc[role] = [];
      }
      acc[role].push(assignment);
      return acc;
    }, {});
  };

  const renderGroup = (group, zoneId) => {
    const groupedAssignments = groupAssignmentsByRole(group.assignments);
    return (
      <div key={group.id} className="org-group">
        <div className="org-item-header">
          <div className="org-item-info">
            <i className="fas fa-users"></i>
            <span className="org-item-name">{group.name}</span>
            <span className="org-badge">{t('organization.badges.group')}</span>
          </div>
          <div className="org-item-actions">
            <button className="btn-icon btn-manage" onClick={() => handleManageMembers(group)} title={t('organization.actions.manageMembers')}>
              <i className="fas fa-user-plus"></i> {t('organization.actions.manageMembers')}
            </button>
            <button className="btn-icon btn-edit" onClick={() => handleEdit('group', group, { type: 'zone', id: zoneId })} title={t('organization.actions.edit')}>
              <i className="fas fa-edit"></i> {t('organization.actions.edit')}
            </button>
            <button className="btn-icon btn-delete" onClick={() => handleDelete('group', group.id, zoneId)} title={t('organization.actions.delete')}>
              <i className="fas fa-trash"></i> {t('organization.actions.delete')}
            </button>
          </div>
        </div>
        <div className="org-item-details">
          <div className="org-detail-item">
            <span className="detail-label">{t('organization.detail.groupManager')}</span>
            <span className="detail-value">{renderManagerLabel(group)}</span>
          </div>
          <div className="org-member-stats">
            <span className="stat-item">
              <i className="fas fa-star"></i> {t('organization.stats.core')}: {group.member_stats?.core_members || 0}
            </span>
            <span className="stat-item">
              <i className="fas fa-user"></i> {t('organization.stats.full')}: {group.member_stats?.full_members || 0}
            </span>
            <span className="stat-item">
              <i className="fas fa-user-friends"></i> {t('organization.stats.associate')}: {group.member_stats?.associate_members || 0}
            </span>
            <span className="stat-item total">
              {t('organization.stats.total')}: {group.member_stats?.total_members || 0}
            </span>
          </div>
          <div className="group-member-names">
            {Object.entries(roleLabelMap).map(([roleKey, label]) => (
              <div key={roleKey} className="group-member-section">
                <div className="group-member-section-heading">
                  {label} ({groupedAssignments[roleKey]?.length || 0})
                </div>
                {groupedAssignments[roleKey] && groupedAssignments[roleKey].length > 0 ? (
                  groupedAssignments[roleKey].map(assignment => (
                    <div key={assignment.id} className="group-member-row">
                      <span className="group-member-name">{renderManagerLabel(assignment.member)}</span>
                      <button
                        className="btn-icon btn-delete group-member-remove"
                        onClick={() => handleInlineRemoveAssignment(assignment.id)}
                        title="移除組員"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="group-member-empty">{t('organization.empty')}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderZone = (zone, districtId) => (
    <div key={zone.id} className="org-zone">
      <div className="org-item-header" onClick={() => toggleZone(zone.id)}>
        <div className="org-item-info">
          <i className={`fas fa-chevron-${expandedZones[zone.id] ? 'down' : 'right'}`}></i>
          <i className="fas fa-layer-group"></i>
          <span className="org-item-name">{zone.name}</span>
          <span className="org-badge zone-badge">{t('organization.badges.zone')}</span>
          <span className="org-count">{t('organization.actions.groupCount', { count: zone.groups?.length || 0 })}</span>
        </div>
        <div className="org-item-actions" onClick={(e) => e.stopPropagation()}>
          <button 
            className="btn-icon btn-add" 
            onClick={() => handleAddNew('group', { type: 'zone', id: zone.id, name: zone.name })}
            title={t('organization.actions.addGroup')}
          >
            <i className="fas fa-plus"></i> {t('organization.actions.addGroup')}
          </button>
          <button className="btn-icon btn-edit" onClick={() => handleEdit('zone', zone, { type: 'district', id: districtId })} title={t('organization.actions.edit')}>
            <i className="fas fa-edit"></i> {t('organization.actions.edit')}
          </button>
          <button className="btn-icon btn-delete" onClick={() => handleDelete('zone', zone.id, districtId)} title={t('organization.actions.delete')}>
            <i className="fas fa-trash"></i> {t('organization.actions.delete')}
          </button>
        </div>
      </div>
      <div className="org-item-details">
        <div className="org-detail-item">
          <span className="detail-label">{t('organization.detail.zoneManager')}</span>
          <span className="detail-value">{renderManagerLabel(zone)}</span>
        </div>
      </div>
      {expandedZones[zone.id] && (
        <div className="org-children">
          {zone.groups && zone.groups.length > 0 ? (
            zone.groups.map(group => renderGroup(group, zone.id))
          ) : (
            <div className="empty-state">{t('organization.filters.groupEmpty')}</div>
          )}
        </div>
      )}
    </div>
  );

  const renderDistrict = (district) => (
    <div key={district.id} className="org-district">
      <div className="org-item-header" onClick={() => toggleDistrict(district.id)}>
        <div className="org-item-info">
          <i className={`fas fa-chevron-${expandedDistricts[district.id] ? 'down' : 'right'}`}></i>
          <i className="fas fa-map-marked-alt"></i>
          <span className="org-item-name">{district.name}</span>
          <span className="org-badge district-badge">{t('organization.badges.district')}</span>
          <span className="org-count">{t('organization.actions.zoneCount', { count: district.zones?.length || 0 })}</span>
        </div>
        <div className="org-item-actions" onClick={(e) => e.stopPropagation()}>
          <button 
            className="btn-icon btn-add" 
            onClick={() => handleAddNew('zone', { type: 'district', id: district.id, name: district.name })}
            title={t('organization.actions.addZone')}
          >
            <i className="fas fa-plus"></i> {t('organization.actions.addZone')}
          </button>
          <button className="btn-icon btn-edit" onClick={() => handleEdit('district', district)} title={t('organization.actions.edit')}>
            <i className="fas fa-edit"></i> {t('organization.actions.edit')}
          </button>
          <button className="btn-icon btn-delete" onClick={() => handleDelete('district', district.id)} title={t('organization.actions.delete')}>
            <i className="fas fa-trash"></i> {t('organization.actions.delete')}
          </button>
        </div>
      </div>
      <div className="org-item-details">
        <div className="org-detail-item">
          <span className="detail-label">{t('organization.detail.districtManager')}</span>
          <span className="detail-value">{renderManagerLabel(district)}</span>
        </div>
      </div>
      {expandedDistricts[district.id] && (
        <div className="org-children">
          {district.zones && district.zones.length > 0 ? (
            district.zones.map(zone => renderZone(zone, district.id))
          ) : (
            <div className="empty-state">{t('organization.filters.zoneEmpty')}</div>
          )}
        </div>
      )}
    </div>
  );

  const findDuplicateName = (type, name, excludeId = null) => {
    const normalized = (name || '').trim().toLowerCase();
    if (!normalized) return true;
    if (type === 'district') {
      return districts.some((d) => d.id !== excludeId && (d.name || '').trim().toLowerCase() === normalized);
    }
    if (type === 'zone') {
      return districts.some((d) =>
        (d.zones || []).some((z) => z.id !== excludeId && (z.name || '').trim().toLowerCase() === normalized)
      );
    }
    if (type === 'group') {
      return districts.some((d) =>
        (d.zones || []).some((z) =>
          (z.groups || []).some((g) => g.id !== excludeId && (g.name || '').trim().toLowerCase() === normalized)
        )
      );
    }
    return false;
  };

  const handleSaveModal = async (data) => {
    try {
      const name = (data.name || '').trim();
      const typeLabel = t(`organization.types.${modalType}`, { defaultValue: modalType });
      if (!name) {
        dispatch(addNotification({
          type: 'error',
          title: t('common.errorTitle'),
          message: t('organization.emptyName', { type: typeLabel }),
        }));
        return;
      }
      if (findDuplicateName(modalType, name, editingItem?.id)) {
        dispatch(addNotification({
          type: 'error',
          title: t('common.errorTitle'),
          message: t('organization.duplicateName', { type: typeLabel, name }),
        }));
        return;
      }

      // Build manager fields - either member ID or custom name
      const managerFields = data.managerId 
        ? { manager_id: parseInt(data.managerId), manager_name: null }
        : { manager_id: null, manager_name: data.managerName || null };

      if (editingItem) {
        // Update existing item
        if (modalType === 'district') {
          await organizationService.updateDistrict(editingItem.id, {
            name,
            ...managerFields,
            description: data.description
          });
        } else if (modalType === 'zone') {
          await organizationService.updateZone(editingItem.id, {
            name,
            ...managerFields,
            description: data.description
          });
        } else if (modalType === 'group') {
          await organizationService.updateGroup(editingItem.id, {
            name,
            ...managerFields,
            description: data.description
          });
        }
      } else {
        // Create new item
        if (modalType === 'district') {
          await organizationService.createDistrict({
            name,
            ...managerFields,
            description: data.description || ''
          });
        } else if (modalType === 'zone') {
          await organizationService.createZone({
            district_id: selectedParent.id,
            name,
            ...managerFields,
            description: data.description || ''
          });
        } else if (modalType === 'group') {
          await organizationService.createGroup({
            zone_id: selectedParent.id,
            name,
            ...managerFields,
            description: data.description || ''
          });
        }
      }
      
      // Reload data after successful save
      await loadOrganizationData();
      setShowAddModal(false);
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving:', err);
      const detail = err?.response?.data?.detail;
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message: typeof detail === 'string' ? detail : t('organization.saveError')
      }));
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border"></div>
        <p>{t('organization.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button className="btn btn-primary" onClick={loadOrganizationData}>
          {t('organization.reload')}
        </button>
      </div>
    );
  }

  return (
    <div className="organization-structure-container">
      <div className="page-header">
        <div className="header-left">
          <h1>{t('organization.title')}</h1>
          <p className="page-subtitle">{t('organization.subtitle')}</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => handleAddNew('district')}
          >
            <i className="fas fa-plus"></i> {t('organization.actions.addDistrict')}
          </button>
        </div>
      </div>

      <div className="org-legend">
        <div className="legend-item">
          <i className="fas fa-map-marked-alt"></i> {t('organization.legend.district')}
        </div>
        <div className="legend-item">
          <i className="fas fa-layer-group"></i> {t('organization.legend.zone')}
        </div>
        <div className="legend-item">
          <i className="fas fa-users"></i> {t('organization.legend.group')}
        </div>
      </div>

      {/* Filter Section - 3 Cascading Autocomplete Selectors */}
      <div className="org-filter">
        {/* District Filter */}
        <div className="filter-group" ref={districtRef}>
          <label>{t('organization.filters.districtLabel')}</label>
          <div className="filter-autocomplete">
            <input 
              type="text"
              className={`filter-search ${selectedDistrict ? 'has-selection' : ''}`}
              placeholder={t('organization.filters.districtPlaceholder')}
              value={districtSearch}
              onChange={(e) => {
                setDistrictSearch(e.target.value);
                setShowDistrictDropdown(true);
                if (!e.target.value) {
                  handleClearDistrict();
                }
              }}
              onFocus={() => setShowDistrictDropdown(true)}
            />
            {selectedDistrict && (
              <button className="filter-clear-btn" onClick={handleClearDistrict}>
                <i className="fas fa-times"></i>
              </button>
            )}
            {showDistrictDropdown && districtSearch && (
              <div className="filter-dropdown">
                {getDistrictSuggestions().length > 0 ? (
                  getDistrictSuggestions().map(district => (
                    <div 
                      key={district.id}
                      className="filter-dropdown-item"
                      onClick={() => handleSelectDistrict(district)}
                    >
                      <span className="filter-item-name">{district.name}</span>
                      <span className="filter-item-type type-district">{t('organization.badges.district')}</span>
                    </div>
                  ))
                ) : (
                  <div className="filter-dropdown-empty">{t('organization.filters.districtEmpty')}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Zone Filter */}
        <div className="filter-group" ref={zoneRef}>
          <label>{t('organization.filters.zoneLabel')}</label>
          <div className="filter-autocomplete">
            <input 
              type="text"
              className={`filter-search ${selectedZone ? 'has-selection' : ''}`}
              placeholder={t('organization.filters.zonePlaceholder')}
              value={zoneSearch}
              onChange={(e) => {
                setZoneSearch(e.target.value);
                setShowZoneDropdown(true);
                if (!e.target.value) {
                  handleClearZone();
                }
              }}
              onFocus={() => setShowZoneDropdown(true)}
            />
            {selectedZone && (
              <button className="filter-clear-btn" onClick={handleClearZone}>
                <i className="fas fa-times"></i>
              </button>
            )}
            {showZoneDropdown && zoneSearch && (
              <div className="filter-dropdown">
                {getZoneSuggestions().length > 0 ? (
                  getZoneSuggestions().map(zone => (
                    <div 
                      key={zone.id}
                      className="filter-dropdown-item"
                      onClick={() => handleSelectZone(zone)}
                    >
                      <span className="filter-item-name">{zone.name}</span>
                      <span className="filter-item-type type-zone">{t('organization.badges.zone')}</span>
                    </div>
                  ))
                ) : (
                  <div className="filter-dropdown-empty">{t('organization.filters.zoneEmpty')}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Group Filter */}
        <div className="filter-group" ref={groupRef}>
          <label>{t('organization.filters.groupLabel')}</label>
          <div className="filter-autocomplete">
            <input 
              type="text"
              className={`filter-search ${selectedGroup ? 'has-selection' : ''}`}
              placeholder={t('organization.filters.groupPlaceholder')}
              value={groupSearch}
              onChange={(e) => {
                setGroupSearch(e.target.value);
                setShowGroupDropdown(true);
                if (!e.target.value) {
                  handleClearGroup();
                }
              }}
              onFocus={() => setShowGroupDropdown(true)}
            />
            {selectedGroup && (
              <button className="filter-clear-btn" onClick={handleClearGroup}>
                <i className="fas fa-times"></i>
              </button>
            )}
            {showGroupDropdown && groupSearch && (
              <div className="filter-dropdown">
                {getGroupSuggestions().length > 0 ? (
                  getGroupSuggestions().map(group => (
                    <div 
                      key={group.id}
                      className="filter-dropdown-item"
                      onClick={() => handleSelectGroup(group)}
                    >
                      <span className="filter-item-name">{group.name}</span>
                      <span className="filter-item-type type-group">{t('organization.badges.group')}</span>
                    </div>
                  ))
                ) : (
                  <div className="filter-dropdown-empty">{t('organization.filters.groupEmpty')}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Clear All Filter Button */}
        {(selectedDistrict || selectedZone || selectedGroup) && (
          <div className="filter-group filter-clear-all">
            <button className="btn btn-outline-secondary btn-clear-filter" onClick={handleClearDistrict}>
              <i className="fas fa-times"></i> {t('organization.filters.clearAll')}
            </button>
          </div>
        )}
      </div>

      <div className="organization-tree">
        {filteredDistricts.length > 0 ? (
          filteredDistricts.map(district => renderDistrict(district))
        ) : (
          <div className="empty-state-large">
            <i className="fas fa-sitemap"></i>
            <p>{t('organization.empty')}</p>
            <button className="btn btn-primary" onClick={() => handleAddNew('district')}>
              {t('organization.addFirst')}
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <AddEditModal
          type={modalType}
          parent={selectedParent}
          editingItem={editingItem}
          onClose={() => {
            setShowAddModal(false);
            setEditingItem(null);
          }}
          onSave={handleSaveModal}
        />
      )}

      {/* Group Members Modal */}
      {showMemberModal && activeGroupForMembers && (
        <GroupMembersModal
          group={activeGroupForMembers}
          onClose={closeMemberModal}
          onMembersChanged={handleMembersUpdated}
        />
      )}
    </div>
  );
};

export default OrganizationStructure;
